import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Contract } from 'ethers';
import { ReactComponent as MetamaskIcon } from '@blink/assets/src/icons/metamask.svg';
import { ReactComponent as WalletConnectIcon } from '@blink/assets/src/icons/walletConnect.svg';
import { ReactComponent as RabbyIcon } from '@blink/assets/src/icons/rabby.svg';
import { ReactComponent as EthereumIcon } from '@blink/assets/src/icons/ethereum.svg';
import { LOCAL_STORAGE, LocalStorage } from '@blink/components/src/utils';
import { WALLET } from '@blink/components/src/types';

import { PATHS } from 'src/routes/paths';

import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { useWalletContract } from '@/shared/hooks/useWalletContract';
import { useGetVaultMarginAccounts } from '@/shared/hooks/useGetVaultMarginAccounts';
import AgreementABI from '@/shared/abi/AgreementABI.json';
import { ACCOUNT_STATUS } from '@/shared/api/vault';

type UseCheckVaultMarginAccountProps = {
    address?: string;
};

type WalletIcons = Partial<{
    [key in WALLET]: JSX.Element;
}>;

const walletIcons: WalletIcons = {
    [WALLET.META_MASK]: <MetamaskIcon />,
    [WALLET.WALLET_CONNECT]: <WalletConnectIcon />,
    [WALLET.RABBY]: <RabbyIcon />,
};

export const useCheckVaultMarginAccount = ({ address }: UseCheckVaultMarginAccountProps) => {
    const navigate = useNavigate();

    const dispatch = useDispatch();

    const { data: marginAccounts, fetchMarginAccounts, loading } = useGetVaultMarginAccounts();

    const currentAccount = useMemo(
        () =>
            marginAccounts?.find(
                (account) => account.agreementAddress.toLowerCase() === address?.toLowerCase(),
            ),
        [marginAccounts, address],
    );

    const [status, setStatus] = useState<boolean | null>(false);

    const vault = useWalletContract({
        address: address as string,
        abi: AgreementABI,
    });

    const { status: isSuspend, getSuspend } = useGetVaultStatus(vault);

    const handleOnClose = async () => {
        fetchMarginAccounts();

        navigate(`${PATHS.VAULTS_MANAGE}`);
    };

    const walletType: WALLET | null = LocalStorage.getItem(LOCAL_STORAGE.WALLET) as WALLET;

    const getWalletIcon = (): JSX.Element => {
        return walletIcons[walletType as WALLET] || <EthereumIcon />;
    };

    const description = `Confirm action from ${walletType || 'your wallet'} to proceed`;
    const icon = getWalletIcon();

    const showActivateMarginAccountModal = () => {
        dispatch(
            showModalAction({
                modalName: ModalName.ACTIVATE_YOUR_VAULT_MODAL,
                modalTitle: 'Activate Your Vault',
                modalProps: {
                    address,
                    vault,
                    isSuspend,
                    description,
                    icon,
                },
                onClose: handleOnClose,
            }),
        );

        setStatus(false);
    };

    const showVaultIsBeenActivatedModal = () => {
        dispatch(
            showModalAction({
                modalName: ModalName.VAULT_IS_BEEN_ACTIVATED_MODAL,
                modalProps: {
                    address,
                    isSuspend,
                },
                onClose: handleOnClose,
            }),
        );

        setStatus(false);
    };

    useEffect(() => {
        if (!vault) return;
        getSuspend();
    }, [marginAccounts, vault, getSuspend]);

    useEffect(() => {
        if (currentAccount && currentAccount.status === ACCOUNT_STATUS.OPENED && isSuspend) {
            return setStatus(true);
        }

        if (!marginAccounts || !address || !vault || isSuspend === null || loading) {
            return setStatus(false);
        }

        if (marginAccounts && !currentAccount && !isSuspend) {
            return showActivateMarginAccountModal();
        }

        if (!currentAccount && isSuspend) {
            return showVaultIsBeenActivatedModal();
        }

        if (currentAccount && currentAccount.status !== ACCOUNT_STATUS.OPENED) {
            return showVaultIsBeenActivatedModal();
        }

        if (!isSuspend) {
            return showVaultIsBeenActivatedModal();
        }
    }, [
        currentAccount,
        marginAccounts,
        address,
        vault,
        isSuspend,
        loading,
        currentAccount?.status,
    ]);

    return status;
};

const useGetVaultStatus = (vault: Contract | null) => {
    const [status, setStatus] = useState<boolean | null>(null);

    const getSuspend = useCallback(async () => {
        if (!vault) {
            return;
        }

        try {
            const isSuspend = await vault.suspended();
            setStatus(isSuspend);
        } catch (error) {
            console.error(error);
        }
    }, [vault]);

    useEffect(() => {
        getSuspend();
    }, [getSuspend]);

    return {
        status,
        getSuspend,
    };
};
